import type { FC, MouseEvent } from 'react';
import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { isBuildIn } from '../build-in';
import { LoadingIcon } from '../components/loading-icon';
import { cx } from '../cx';
import { isInAlipay } from '../url';
import { useIsDarkMode } from '../utils/get-dark';
import { ResourceLoader } from '../utils/resource-loader';
import './index.css';
import { mermaidTask } from './mermaid-task';

// 创建全局 mermaid 脚本加载器
const mermaidUrls = isBuildIn()
  ? [
      'https://cdn.jsdelivr.net/npm/mermaid@10.6.1/dist/mermaid.min.js',
      'https://cdn2.flowus.cn/emoji/mermaid.min.js',
    ]
  : [
      'https://lib.baomitu.com/mermaid/10.6.1/mermaid.min.js',
      'https://cdn2.flowus.cn/emoji/mermaid.min.js',
    ];

const mermaidLoader = new ResourceLoader(mermaidUrls, 'script');

interface Props {
  text?: string;
  className?: string;
  onRender?: (status: boolean) => void;
  onClick?: (event: MouseEvent) => void;
}
export const Mermaid: FC<Props> = memo((props) => {
  const { text = '', className, onRender, onClick } = props;
  const ref = useRef<HTMLDivElement>(null);
  const [isError, setIsError] = useState(false);
  const refId = useRef(v4());
  const isDarkMode = useIsDarkMode();
  const [mermaid, setMermaid] = useState<any>((window as any).mermaid);
  const [isLoading, setIsLoading] = useState(false);

  // 动态加载 mermaid 脚本
  useEffect(() => {
    if (isInAlipay) {
      return;
    }

    // 如果已经加载完成，直接设置
    if ((window as any).mermaid) {
      setMermaid((window as any).mermaid);
      return;
    }

    let mounted = true;
    setIsLoading(true);

    // 使用 ResourceLoader 加载脚本
    mermaidLoader
      .load()
      .then(() => {
        if (!mounted) return;

        // 脚本加载成功后，验证 window.mermaid 是否可用
        if ((window as any).mermaid) {
          setMermaid((window as any).mermaid);
          setIsLoading(false);
        } else {
          console.error('[Mermaid] Script loaded but window.mermaid not found');
          setIsLoading(false);
        }
      })
      .catch((err) => {
        if (!mounted) return;
        console.error('[Mermaid] Failed to load:', err);
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  // 使用 useLayoutEffect 确保 DOM 更新后立即执行
  useLayoutEffect(() => {
    const dom = ref.current;
    if (dom && mermaid && !isLoading) {
      if (!text) {
        setIsError(true);
        dom.innerHTML = '';
        onRender?.(false);
        return;
      }
      mermaid?.mermaidAPI.initialize({ theme: isDarkMode ? 'dark' : 'neutral' });
      mermaidTask.render({
        text,
        id: `mermaid-${refId.current}`,
        onSuccess: (res) => {
          setIsError(false);
          dom.innerHTML = res;
          onRender?.(true);
        },
        onError: (err) => {
          console.error('[Mermaid] Render error:', err);
          setIsError(true);
          dom.innerHTML = err;
          onRender?.(false);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, mermaid, isLoading, isDarkMode]);

  if (!text) {
    return null;
  }

  // 显示加载中状态
  if (isLoading || !mermaid) {
    return (
      <div className={cx('flex justify-center items-center py-4', className)}>
        <LoadingIcon size="middle" />
      </div>
    );
  }

  return (
    <>
      <div
        onClick={onClick}
        key={refId.current}
        className={cx('flex justify-center', isError && 'text-red text-t3', className)}
        ref={ref}
      />
      {isError && ref.current?.innerText.includes('dynamically') && (
        <div className="text-t3 text-red text-center">资源加载失败，请尝试重新登录</div>
      )}
    </>
  );
});
